import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatDivider } from '@angular/material/divider'
import { MinutesStylingComponent } from '../minutes-styling/minutes-styling.component'
import { FormsModule } from '@angular/forms'
import { AsyncPipe } from '@angular/common'
import { SettingsService } from '../settings.service'
import { distinctUntilChanged, filter, map } from 'rxjs'
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { MatOption } from '@angular/material/autocomplete'
import { MatSelect } from '@angular/material/select'
import { CalendarProviderName, User, UsersService, UserWithOrganization } from '../users.service'
import { Organization } from '../organizations.service'
import { MatButton } from '@angular/material/button'
import { ToastService } from '../toast.service'
import { CallsService, MinutesPassive, MinutesStyling, MetricType } from '../calls.service'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { ActivatedRoute } from '@angular/router'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatChip } from '@angular/material/chips'
import { MatTooltip } from '@angular/material/tooltip'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import { SpinnerComponent } from '../spinner/spinner.component'
import { LoggerService } from '../logger.service'
import { JoinSettingsComponent } from '../join-settings/join-settings.component'
import { MatCheckbox } from '@angular/material/checkbox'
import { MetricsService } from '../metrics.service'
import { TimezoneSelectorComponent } from '../timezone-selector/timezone-selector.component'
import { InheritedSettingName, ResolvedSettings } from '../client-settings'

export type JoinSettings = {
  joinMeetingDeclined: boolean
  joinMeetingTentativelyAccepted: boolean
  joinMeetingUnresponded: boolean
  joinMeetingExternal: boolean
  joinMeetingInternal: boolean
}

const SectionTranslationKeyMap: { [key: string]: string } = {
  general: 'SETTINGS.GENERAL_SETTINGS',
  language: 'SETTINGS.LANGUAGE_SETTINGS',
  calendar: 'SETTINGS.CALENDAR_SETTINGS',
  assistant: 'SETTINGS.ASSISTANT_SETTINGS',
  'minutes-styling': 'SETTINGS.MINUTING_SETTINGS',
  notifications: 'SETTINGS.NOTIFICATION_SETTINGS',
  transcript: 'SETTINGS.TRANSCRIPT_SETTINGS',
  sharing: 'SETTINGS.SHARING_SETTINGS',
}

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    TranslatePipe,
    TranslateDirective,
    MatDivider,
    MinutesStylingComponent,
    FormsModule,
    AsyncPipe,
    SpinnerComponent,
    MatFormField,
    MatInput,
    MatLabel,
    MatError,
    MatOption,
    MatSelect,
    MatButton,
    ToolbarComponent,
    MatSlideToggle,
    MatChip,
    MatTooltip,
    JoinSettingsComponent,
    MatCheckbox,
    TimezoneSelectorComponent,
  ],
  templateUrl: './settings-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './settings-page.component.scss',
})
export class SettingsPageComponent implements OnInit {
  settingsService = inject(SettingsService)
  usersService = inject(UsersService)
  route = inject(ActivatedRoute)
  toastService = inject(ToastService)
  translateService = inject(TranslateService)
  callsService = inject(CallsService)
  dialog = inject(MatDialog)
  userSettings$ = this.settingsService.getUserSettings()
  currentUser$ = this.usersService.currentUser$
  organizationSettings$ = this.settingsService.getOrganizationSettings()
  userData!: User & { joinSettings: JoinSettings }
  organizationData!: Organization
  logger = inject(LoggerService)
  metricsService = inject(MetricsService)
  saveUserSettingsMutation = this.settingsService.updateUserSettings()
  saveOrganizationSettingsMutation = this.settingsService.updateOrganizationSettings()
  unlinkCalendarProviderMutation = this.settingsService.unlinkCalendarProviderMutation()
  section$ = this.route.params.pipe(
    map((params) => params['section']),
    distinctUntilChanged()
  )

  ngOnInit() {
    this.setUserData()
    this.setOrganizationData()
    this.initializeUserLocale()
  }

  getSectionTranslationKey(section: string) {
    const translationKey = SectionTranslationKeyMap[section]
    if (translationKey === undefined) {
      this.logger.error(`No translation key found for section: ${section}`)
      return 'COMMON.SETTINGS'
    }
    return translationKey
  }

  private initializeUserLocale() {
    this.userSettings$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((data) => {
        if (data?.locale) {
          const localeMap: { [key: string]: string } = {
            EN_US: 'en_US',
            EN_GB: 'en_GB',
          }
          const translationKey = localeMap[data.locale]
          if (translationKey) {
            this.translateService.use(translationKey)
          }
        }
      })
  }

  private setUserData() {
    this.userSettings$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((data) => (this.userData = data!))
  }

  private setOrganizationData() {
    this.organizationSettings$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((data) => {
        this.organizationData = data!
        this.resolvedMinutesStyling = this.buildResolvedMinutesStyling()
      })
  }

  /** The value that applies right now: this organization's override, or the client's default. */
  settingValue<K extends InheritedSettingName>(setting: K): ResolvedSettings[K] {
    const override = (this.organizationData as Record<string, unknown>)[setting]
    return (override ?? this.organizationData.client[setting]) as ResolvedSettings[K]
  }

  setSetting<K extends InheritedSettingName>(setting: K, value: ResolvedSettings[K]) {
    ;(this.organizationData as Record<string, unknown>)[setting] = value
  }

  async updateUser() {
    if (this.userData.locale) {
      const localeMap: { [key: string]: string } = {
        EN_US: 'en_US',
        EN_GB: 'en_GB',
      }
      const translationKey = localeMap[this.userData.locale]
      if (translationKey) {
        this.translateService.use(translationKey)
      }
    }

    await this.saveUserSettingsMutation.mutateAsync(this.userData)
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
  }

  async updateOrganization() {
    await this.saveOrganizationSettingsMutation.mutateAsync(this.organizationData)
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
  }

  resolvedMinutesStyling!: MinutesStyling

  private buildResolvedMinutesStyling(): MinutesStyling {
    return {
      minutesVerboseness: this.settingValue('minutesVerboseness'),
      minutesPassive: this.settingValue('minutesPassive'),
      minutesAnonymization: this.settingValue('minutesAnonymization'),
      minutesAttribution: this.settingValue('minutesAttribution'),
      minutesNameFormat: this.settingValue('minutesNameFormat'),
      minutesDateFormat: this.settingValue('minutesDateFormat'),
      minutesLanguage: this.settingValue('minutesLanguage'),
      minutesTimeFormat: this.settingValue('minutesTimeFormat'),
    }
  }

  async updateMinutesStyling(minutesStyling: Partial<MinutesStyling>) {
    this.organizationData = { ...this.organizationData, ...minutesStyling }
    this.resolvedMinutesStyling = this.buildResolvedMinutesStyling()
    await this.metricsService.sendMetric(null, MetricType.CHANGE_MINUTING_STYLES, minutesStyling)
  }

  async unlinkCalendarProvider(provider: CalendarProviderName) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('SETTINGS.UNLINK_CONFIRMATION.DIALOG_TITLE'),
        description: this.translateService.instant('SETTINGS.UNLINK_CONFIRMATION.DIALOG_BODY'),
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.unlinkCalendarProviderMutation.mutateAsync({ provider })
        this.toastService.show('SETTINGS.UNLINK_SUCCESSFUL')
      }
    })
  }

  linkCalendarProvider(provider: CalendarProviderName) {
    window.location.href = `/api/users/me/calendar-connect?provider=${provider}`
  }

  isPrimaryEmail(providerSpecificEmail: string) {
    return this.userData.email === providerSpecificEmail
  }

  isGoogleProvider(provider: CalendarProviderName): boolean {
    return provider === 'GOOGLE' || provider.includes('GOOGLE')
  }

  protected readonly MinutesPassive = MinutesPassive

  async toggleJoinSettingsFreezing() {
    const initialValue = this.settingValue('freezeJoinSettings')
    this.setSetting('freezeJoinSettings', !this.settingValue('freezeJoinSettings'))
    if (initialValue) {
      this.toastService.show('SETTINGS.JOIN_SETTINGS_UNFROZEN')
      await this.updateOrganization()
    } else {
      this.confirmFreezeJoinSettings(initialValue)
    }
  }

  private confirmFreezeJoinSettings(initialValue: boolean) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('SETTINGS.CONFIRM_FREEZE_JOIN_SETTINGS_DIALOG.TITLE'),
        description: this.translateService.instant('SETTINGS.CONFIRM_FREEZE_JOIN_SETTINGS_DIALOG.DESCRIPTION'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.actuallyFreezeJoinSettings()
      } else {
        this.setSetting('freezeJoinSettings', initialValue)
      }
    })
  }

  private async actuallyFreezeJoinSettings() {
    this.setSetting('freezeJoinSettings', true)
    await this.updateOrganization()
  }

  async updateSendUpcomingCallReminderEmail(value: boolean) {
    this.userData.sendUpcomingCallReminderEmail = value
    await this.updateUser()
  }

  async updateNotifyWhenPostProcessingCompletes(value: boolean) {
    this.userData.notifyWhenPostProcessingCompletes = value
    await this.updateUser()
  }

  async updateSendWeeklyDigestEmail(value: boolean | null) {
    this.userData.sendWeeklyDigestEmail = value
    await this.updateUser()
  }

  async updateSendSoftDeletionWarningEmail(value: boolean | null) {
    this.userData.sendSoftDeletionWarningEmail = value
    await this.updateUser()
  }

  async updateSendSoftDeletionConfirmationEmail(value: boolean | null) {
    this.userData.sendSoftDeletionConfirmationEmail = value
    await this.updateUser()
  }

  async updateSendAssistantAccessDeniedEmail(value: boolean | null) {
    this.userData.sendAssistantAccessDeniedEmail = value
    await this.updateUser()
  }

  async onSoftDeletionWarningEmailChange() {
    if (this.settingValue('sendSoftDeletionWarningEmail') && !this.settingValue('softDeletionWarningHours')) {
      this.setSetting('softDeletionWarningHours', 2)
    }
    await this.updateOrganization()
  }

  async setSoftDeletionWarningHours(eventValue: string) {
    const numberValue = parseInt(eventValue, 10)
    if (isNaN(numberValue) || numberValue < 1) {
      this.setSetting('softDeletionWarningHours', 2)
    } else {
      this.setSetting('softDeletionWarningHours', numberValue)
    }
    await this.updateOrganization()
  }
}
